import { Box, Flex, Icon, Text } from '@chakra-ui/react'
import { Building2, ChevronDown, ChevronUp } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMenuStyles } from '~/components/shared/componentStyles/Menu.style'
import { MenuPositioningWrapper } from '~/components/shared/Dropdown/Menu'
import { PbxPicker, usePbxSwitcher } from '~/components/shared/PbxPicker'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { useGetPbxList } from '~/queries/pbxQueries'
import { ZIndex } from '~/types/ThemeTypes'
import { castRef } from '~/utils/refUtils'

import { keyboardFocusRing } from '@mangovoice/design-system'

const POPOVER_WIDTH = '20rem'

export const PbxHeaderSwitcher = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)

  const { data: pbxListData } = useGetPbxList({
    apiParams: { limit: 1 },
  })
  const hasMultiplePbxs = (pbxListData?.count ?? 0) > 1

  // Hook is called at the component level so the underlying state survives
  // the popover close — the user-confirm callback fires after the popover
  // unmounts its content, and would otherwise hit a stale/no-op setState.
  const { currentPbx, handleSelect } = usePbxSwitcher({
    onAfterSwitchInitiated: () => setIsOpen(false),
  })

  const handleOutsideClick = useCallback(() => setIsOpen(false), [])

  useOutsideClick({
    ref: castRef(containerRef),
    handler: handleOutsideClick,
    enabled: isOpen,
  })

  // Close on Escape so keyboard users can dismiss the popover. We stop
  // propagation so parent Escape handlers (e.g. the Admin drawer) don't
  // also dismiss themselves in the same keystroke.
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const menuStyles = useMenuStyles()

  if (!hasMultiplePbxs || !currentPbx) return null

  return (
    <MenuPositioningWrapper ref={containerRef}>
      <Flex
        as="button"
        align="center"
        gap="2"
        h="2.5rem"
        px="4"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.default"
        bg="bg.panel"
        cursor="pointer"
        transition="background-color 0.15s"
        _hover={{ bg: 'bg.muted' }}
        {...keyboardFocusRing}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={t('pbxHeaderSwitcher.ariaLabel', 'Switch PBX')}
        data-testid="pbx-header-switcher-trigger"
      >
        <Icon as={Building2} boxSize="4" color="fg.subtle" />
        <Text
          fontSize="sm"
          fontWeight="500"
          color="fg.default"
          maxWidth="10rem"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {currentPbx.name}
        </Text>
        <Icon
          as={isOpen ? ChevronUp : ChevronDown}
          boxSize="4"
          color="fg.subtle"
        />
      </Flex>

      {isOpen && (
        <Box
          css={menuStyles.container}
          position="absolute"
          top="calc(100% + 0.375rem)"
          right="0"
          zIndex={ZIndex.popover}
          width={POPOVER_WIDTH}
          padding="3"
          data-testid="pbx-header-switcher-popover"
        >
          <Text
            fontSize="xs"
            fontWeight="600"
            color="fg.subtle"
            textTransform="uppercase"
            letterSpacing="wider"
            mb="2"
            px="1"
          >
            {t('pbxHeaderSwitcher.selectPbx', 'Select PBX')}
          </Text>
          <PbxPicker currentPbx={currentPbx} onSelect={handleSelect} />
        </Box>
      )}
    </MenuPositioningWrapper>
  )
}
